import { auth } from '../firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Attach the Firebase ID token to every backend (/api) request, in ONE place, by
// wrapping window.fetch. This covers every call in this file AND the few components
// that call fetch directly, with no per-call-site changes. Only requests to our API
// are touched; all other fetches (Firebase, fonts, etc.) pass through untouched.
// On a 403 we emit a window event the ToastHost listens for ("read-only access").
(function installAuthedFetch() {
  if (typeof window === 'undefined' || window.__slsFetchPatched) return;
  const origFetch = window.fetch.bind(window);
  const isApiUrl = (url) =>
    typeof url === 'string' && (url.startsWith(API_BASE_URL) || url.startsWith('/api/'));

  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    if (!isApiUrl(url)) return origFetch(input, init);
    try {
      const user = auth && auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        init = { ...init, headers: { ...(init.headers || {}), Authorization: `Bearer ${token}` } };
      }
    } catch (_) {
      /* not signed in or token unavailable — send without a token */
    }
    const res = await origFetch(input, init);
    if (res.status === 403) {
      try {
        window.dispatchEvent(new CustomEvent('sls:forbidden', { detail: { url } }));
      } catch (_) {}
    }
    return res;
  };
  window.__slsFetchPatched = true;
})();

// Helper function to handle API responses
async function handleResponse(response) {
  if (!response.ok) {
    let errorMessage = response.statusText;
    try {
      const error = await response.json();
      // FastAPI validation errors have a 'detail' field that can be a string or array
      if (error.detail) {
        if (Array.isArray(error.detail)) {
          // Validation errors are arrays of objects with 'msg' and 'loc' fields
          errorMessage = error.detail.map(err => {
            const field = err.loc ? err.loc.join('.') : 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else {
          errorMessage = error.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
    } catch (e) {
      // If JSON parsing fails, use status text
      errorMessage = response.statusText;
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

// Profile API
export const profileAPI = {
  // Get profile by Firebase UID
  getByFirebaseUID: async (firebaseUID) => {
    const response = await fetch(`${API_BASE_URL}/api/profiles/firebase/${firebaseUID}`);
    return handleResponse(response);
  },

  // Get profile by email
  getByEmail: async (email) => {
    const response = await fetch(`${API_BASE_URL}/api/profiles/email/${email}`);
    return handleResponse(response);
  },

  // No create: accounts are provisioned by an admin invitation (userAPI.createUser).
  // The old POST /api/profiles/ accepted a client-supplied Firebase UID and email, which
  // made it a way to mint accounts without an admin. It has been removed server-side.

  // Update profile by Firebase UID
  updateByFirebaseUID: async (firebaseUID, profileData) => {
    const response = await fetch(`${API_BASE_URL}/api/profiles/firebase/${firebaseUID}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(profileData),
    });
    return handleResponse(response);
  },

  // Update profile by ID
  update: async (profileId, profileData) => {
    const response = await fetch(`${API_BASE_URL}/api/profiles/${profileId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(profileData),
    });
    return handleResponse(response);
  },
};

// Client API
export const clientAPI = {
  // Get all clients
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/api/clients/${queryString ? `?${queryString}` : ''}`;
    const response = await fetch(url);
    return handleResponse(response);
  },

  // Get client by ID
  getById: async (clientId) => {
    const response = await fetch(`${API_BASE_URL}/api/clients/${clientId}`);
    return handleResponse(response);
  },

  // Create client
  create: async (clientData) => {
    try {
      console.log('Creating client with data:', clientData);
      const response = await fetch(`${API_BASE_URL}/api/clients/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(clientData),
      });
      console.log('Response status:', response.status, response.statusText);
      return handleResponse(response);
    } catch (err) {
      console.error('Error in clientAPI.create:', err);
      throw err;
    }
  },

  // Update client
  update: async (clientId, clientData) => {
    const response = await fetch(`${API_BASE_URL}/api/clients/${clientId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(clientData),
    });
    return handleResponse(response);
  },

  // Delete client
  delete: async (clientId) => {
    const response = await fetch(`${API_BASE_URL}/api/clients/${clientId}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Client Contact API
export const clientContactAPI = {
  getByClientId: async (clientId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-contacts/client/${clientId}`);
    return handleResponse(response);
  },

  create: async (contactData) => {
    const response = await fetch(`${API_BASE_URL}/api/client-contacts/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contactData),
    });
    return handleResponse(response);
  },

  update: async (contactId, contactData) => {
    const response = await fetch(`${API_BASE_URL}/api/client-contacts/${contactId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contactData),
    });
    return handleResponse(response);
  },

  delete: async (contactId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-contacts/${contactId}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Client Note API
export const clientNoteAPI = {
  getByClientId: async (clientId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-notes/client/${clientId}`);
    return handleResponse(response);
  },

  create: async (noteData) => {
    const response = await fetch(`${API_BASE_URL}/api/client-notes/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(noteData),
    });
    return handleResponse(response);
  },

  update: async (noteId, noteData) => {
    const response = await fetch(`${API_BASE_URL}/api/client-notes/${noteId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(noteData),
    });
    return handleResponse(response);
  },

  delete: async (noteId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-notes/${noteId}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Client Timeline API
export const clientTimelineAPI = {
  getByClientId: async (clientId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-timeline/client/${clientId}`);
    return handleResponse(response);
  },

  create: async (eventData) => {
    const response = await fetch(`${API_BASE_URL}/api/client-timeline/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventData),
    });
    return handleResponse(response);
  },

  update: async (eventId, eventData) => {
    const response = await fetch(`${API_BASE_URL}/api/client-timeline/${eventId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventData),
    });
    return handleResponse(response);
  },

  delete: async (eventId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-timeline/${eventId}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  parseWithAI: async (text, clientId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-timeline/parse-timeline-with-ai`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text, client_id: clientId }),
    });
    return handleResponse(response);
  },
};

// Contract API
export const contractAPI = {
  getByClientId: async (clientId) => {
    const response = await fetch(`${API_BASE_URL}/api/contracts/client/${clientId}`);
    return handleResponse(response);
  },

  getById: async (contractId) => {
    const response = await fetch(`${API_BASE_URL}/api/contracts/${contractId}`);
    return handleResponse(response);
  },

  create: async (contractData) => {
    const response = await fetch(`${API_BASE_URL}/api/contracts/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contractData),
    });
    return handleResponse(response);
  },

  update: async (contractId, contractData) => {
    const response = await fetch(`${API_BASE_URL}/api/contracts/${contractId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contractData),
    });
    return handleResponse(response);
  },

  delete: async (contractId) => {
    const response = await fetch(`${API_BASE_URL}/api/contracts/${contractId}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  createMilestone: async (contractId, milestoneData) => {
    const response = await fetch(`${API_BASE_URL}/api/contracts/${contractId}/milestones`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(milestoneData),
    });
    return handleResponse(response);
  },
};

// Time Entry API
export const timeEntryAPI = {
  getByClientId: async (clientId, invoiced = null) => {
    const params = invoiced !== null ? `?invoiced=${invoiced}` : '';
    const response = await fetch(`${API_BASE_URL}/api/time-entries/client/${clientId}${params}`);
    return handleResponse(response);
  },

  getById: async (entryId) => {
    const response = await fetch(`${API_BASE_URL}/api/time-entries/${entryId}`);
    return handleResponse(response);
  },

  create: async (entryData) => {
    const response = await fetch(`${API_BASE_URL}/api/time-entries/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(entryData),
    });
    return handleResponse(response);
  },

  update: async (entryId, entryData) => {
    const response = await fetch(`${API_BASE_URL}/api/time-entries/${entryId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(entryData),
    });
    return handleResponse(response);
  },

  delete: async (entryId) => {
    const response = await fetch(`${API_BASE_URL}/api/time-entries/${entryId}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Expense API
export const expenseAPI = {
  getByClientId: async (clientId, invoiceId = null) => {
    const params = invoiceId ? `?client_id=${clientId}&invoice_id=${invoiceId}` : `?client_id=${clientId}`;
    const response = await fetch(`${API_BASE_URL}/api/expenses/${params}`);
    return handleResponse(response);
  },

  getById: async (expenseId) => {
    const response = await fetch(`${API_BASE_URL}/api/expenses/${expenseId}`);
    return handleResponse(response);
  },

  create: async (expenseData) => {
    const response = await fetch(`${API_BASE_URL}/api/expenses/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(expenseData),
    });
    return handleResponse(response);
  },

  update: async (expenseId, expenseData) => {
    const response = await fetch(`${API_BASE_URL}/api/expenses/${expenseId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(expenseData),
    });
    return handleResponse(response);
  },

  delete: async (expenseId) => {
    const response = await fetch(`${API_BASE_URL}/api/expenses/${expenseId}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Invoice API (updated)
export const invoiceAPI = {
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/api/invoices/${queryString ? `?${queryString}` : ''}`;
    const response = await fetch(url);
    return handleResponse(response);
  },

  getById: async (invoiceId) => {
    const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}`);
    return handleResponse(response);
  },

  generateFromTimeEntries: async (clientId, timeEntryIds = [], expenseIds = [], options = {}) => {
    const response = await fetch(`${API_BASE_URL}/api/invoices/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: clientId,
        time_entry_ids: timeEntryIds,
        expense_ids: expenseIds,
        ...options,
      }),
    });
    return handleResponse(response);
  },

  create: async (invoiceData) => {
    const response = await fetch(`${API_BASE_URL}/api/invoices/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(invoiceData),
    });
    return handleResponse(response);
  },

  update: async (invoiceId, invoiceData) => {
    const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(invoiceData),
    });
    return handleResponse(response);
  },

  delete: async (invoiceId) => {
    const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  finalize: async (invoiceId) => {
    const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}/finalize`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  archive: async (invoiceId) => {
    const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}/archive`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  discard: async (invoiceId, options = {}) => {
    const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}/discard`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        delete_time_entries: Boolean(options.delete_time_entries),
        delete_expenses: Boolean(options.delete_expenses),
      }),
    });
    return handleResponse(response);
  },
};

// Client Document API
export const clientDocumentAPI = {
  getByClientId: async (clientId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-documents/client/${clientId}/`);
    return handleResponse(response);
  },

  upload: async (clientId, file, title, description, documentType, uploadedBy) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_id', clientId);
    formData.append('title', title);
    if (description) formData.append('description', description);
    formData.append('document_type', documentType);
    if (uploadedBy) formData.append('uploaded_by', uploadedBy);

    const response = await fetch(`${API_BASE_URL}/api/client-documents/`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse(response);
  },

  update: async (documentId, documentData) => {
    const response = await fetch(`${API_BASE_URL}/api/client-documents/${documentId}/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(documentData),
    });
    return handleResponse(response);
  },

  delete: async (documentId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-documents/${documentId}/`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  download: async (documentId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-documents/${documentId}/download`);
    if (!response.ok) {
      throw new Error('Failed to download document');
    }
    const blob = await response.blob();
    return blob;
  },

  getViewUrl: (documentId) => {
    return `${API_BASE_URL}/api/client-documents/${documentId}/view`;
  },
};

// Client Admin Account API
export const clientAdminAccountAPI = {
  getByClientId: async (clientId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-admin-accounts/client/${clientId}/`);
    return handleResponse(response);
  },

  getById: async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-admin-accounts/${accountId}/`);
    return handleResponse(response);
  },


  create: async (accountData) => {
    const response = await fetch(`${API_BASE_URL}/api/client-admin-accounts/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(accountData),
    });
    return handleResponse(response);
  },

  update: async (accountId, accountData) => {
    const response = await fetch(`${API_BASE_URL}/api/client-admin-accounts/${accountId}/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(accountData),
    });
    return handleResponse(response);
  },

  delete: async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-admin-accounts/${accountId}/`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Client Tech Stack API
export const clientTechStackAPI = {
  getByClientId: async (clientId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-tech-stack/client/${clientId}/`);
    return handleResponse(response);
  },

  create: async (techData) => {
    const response = await fetch(`${API_BASE_URL}/api/client-tech-stack/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(techData),
    });
    return handleResponse(response);
  },

  update: async (techId, techData) => {
    const response = await fetch(`${API_BASE_URL}/api/client-tech-stack/${techId}/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(techData),
    });
    return handleResponse(response);
  },

  delete: async (techId) => {
    const response = await fetch(`${API_BASE_URL}/api/client-tech-stack/${techId}/`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Scope of Work API
export const scopeOfWorkAPI = {
  getAll: async (params = {}) => {
    // Filter out undefined, null, and empty string values
    const cleanParams = {};
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        cleanParams[key] = value;
      }
    });
    const queryString = new URLSearchParams(cleanParams).toString();
    const url = `${API_BASE_URL}/api/scope-of-work/${queryString ? `?${queryString}` : ''}`;
    const response = await fetch(url);
    return handleResponse(response);
  },

  getById: async (scopeId) => {
    const response = await fetch(`${API_BASE_URL}/api/scope-of-work/${scopeId}`);
    return handleResponse(response);
  },

  create: async (scopeData) => {
    const response = await fetch(`${API_BASE_URL}/api/scope-of-work/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(scopeData),
    });
    return handleResponse(response);
  },

  update: async (scopeId, scopeData) => {
    const response = await fetch(`${API_BASE_URL}/api/scope-of-work/${scopeId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(scopeData),
    });
    return handleResponse(response);
  },

  delete: async (scopeId) => {
    const response = await fetch(`${API_BASE_URL}/api/scope-of-work/${scopeId}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  getTemplates: async () => {
    const response = await fetch(`${API_BASE_URL}/api/scope-of-work/templates/sections`);
    return handleResponse(response);
  },

  generateWithAI: async (requestData, userEmail = null) => {
    const url = userEmail 
      ? `${API_BASE_URL}/api/scope-of-work/ai/generate-sow?user_email=${encodeURIComponent(userEmail)}`
      : `${API_BASE_URL}/api/scope-of-work/ai/generate-sow`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });
    return handleResponse(response);
  },

  getRateLimitStatus: async () => {
    const response = await fetch(`${API_BASE_URL}/api/scope-of-work/ai/rate-limit-status`);
    return handleResponse(response);
  },

  regenerateSection: async (scopeId, sectionTitle, userEmail = null) => {
    const url = userEmail 
      ? `${API_BASE_URL}/api/scope-of-work/ai/regenerate-section?user_email=${encodeURIComponent(userEmail)}`
      : `${API_BASE_URL}/api/scope-of-work/ai/regenerate-section`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        scope_id: scopeId,
        section_title: sectionTitle,
      }),
    });
    return handleResponse(response);
  },

  regenerateFull: async (scopeId, userEmail = null) => {
    const url = userEmail 
      ? `${API_BASE_URL}/api/scope-of-work/${scopeId}/ai/regenerate-full?user_email=${encodeURIComponent(userEmail)}`
      : `${API_BASE_URL}/api/scope-of-work/${scopeId}/ai/regenerate-full`;
    const response = await fetch(url, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  previewPDF: async (previewData) => {
    const response = await fetch(`${API_BASE_URL}/api/scope-of-work/preview-pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(previewData),
    });
    // Return blob URL for PDF preview
    if (response.ok) {
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    }
    throw new Error('Failed to generate PDF preview');
  },
};

// Debt Tracker API
export const debtTrackerAPI = {
  // Create Plaid Link token
  createLinkToken: async (userId = null) => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/link-token`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return handleResponse(response);
  },

  // Exchange public token for access token
  exchangePublicToken: async (publicToken, institutionId, institutionName, owner) => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/exchange-public-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        public_token: publicToken,
        institution_id: institutionId,
        institution_name: institutionName,
        owner,
      }),
    });
    return handleResponse(response);
  },

  // Get all debt accounts
  getAccounts: async (owner = null, accountType = null, includePaidOff = false) => {
    const params = new URLSearchParams();
    if (owner) params.append('owner', owner);
    if (accountType) params.append('account_type', accountType);
    if (includePaidOff) params.append('include_paid_off', 'true');
    
    const queryString = params.toString();
    const url = `${API_BASE_URL}/api/debt-tracker/accounts${queryString ? `?${queryString}` : ''}`;
    const response = await fetch(url);
    return handleResponse(response);
  },

  // Get debt account by ID
  getAccount: async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/accounts/${accountId}`);
    return handleResponse(response);
  },

  // Create manual debt account
  createAccount: async (accountData) => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/accounts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(accountData),
    });
    return handleResponse(response);
  },

  // Update debt account
  updateAccount: async (accountId, accountData) => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/accounts/${accountId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(accountData),
    });
    return handleResponse(response);
  },

  // Delete debt account
  deleteAccount: async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/accounts/${accountId}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  // Sync account balance from Plaid
  syncAccount: async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/accounts/${accountId}/sync`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  // Get debt summary
  getSummary: async () => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/summary`);
    return handleResponse(response);
  },

  // Create payment
  createPayment: async (accountId, paymentData) => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/accounts/${accountId}/payments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData),
    });
    return handleResponse(response);
  },

  // Get account payments
  getAccountPayments: async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/accounts/${accountId}/payments`);
    return handleResponse(response);
  },

  // Household-wide recent payments/charges (activity feed)
  getRecentPayments: async (limit = 25) => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/payments/recent?limit=${limit}`);
    return handleResponse(response);
  },

  // Posted credit-card purchases imported through Plaid Transactions
  getCardSpending: async (start, end) => {
    const params = new URLSearchParams({ start, end });
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/card-spending?${params}`);
    return handleResponse(response);
  },

  // Debt-balance history (bad debt only) + live point, for the trend line
  getDebtSnapshots: async (days = 400) => {
    const response = await fetch(`${API_BASE_URL}/api/reports/snapshots?days=${days}`);
    return handleResponse(response);
  },

  // Per-account balances at two snapshot times — attributes a trend move to accounts
  getSnapshotAccounts: async (frm, to) => {
    const response = await fetch(`${API_BASE_URL}/api/reports/snapshots/by-account?frm=${encodeURIComponent(frm)}&to=${encodeURIComponent(to)}`);
    return handleResponse(response);
  },

  // Homes (properties) + persistent sell-vs-keep brainstorm chat
  getProperties: async () => {
    const response = await fetch(`${API_BASE_URL}/api/properties`);
    return handleResponse(response);
  },
  updateProperty: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/api/properties/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  getHomeChat: async () => {
    const response = await fetch(`${API_BASE_URL}/api/properties/home-chat`);
    return handleResponse(response);
  },
  sendHomeChat: async (message) => {
    const response = await fetch(`${API_BASE_URL}/api/properties/home-chat`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message }),
    });
    return handleResponse(response);
  },
  clearHomeChat: async () => {
    const response = await fetch(`${API_BASE_URL}/api/properties/home-chat`, { method: 'DELETE' });
    return handleResponse(response);
  },

  // Property valuations (web-sourced, monthly)
  getValuations: async () => {
    const response = await fetch(`${API_BASE_URL}/api/properties/valuations`);
    return handleResponse(response);
  },
  refreshValuation: async (propId) => {
    const response = await fetch(`${API_BASE_URL}/api/properties/${propId}/valuate`, { method: 'POST' });
    return handleResponse(response);
  },

  // Cash calculator: accounts, per-month bill checkmarks, AI suggestion
  getCashAccounts: async () => {
    const response = await fetch(`${API_BASE_URL}/api/calculator/accounts`);
    return handleResponse(response);
  },
  createCashAccount: async (data) => {
    const response = await fetch(`${API_BASE_URL}/api/calculator/accounts`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  updateCashAccount: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/api/calculator/accounts/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  deleteCashAccount: async (id) => {
    const response = await fetch(`${API_BASE_URL}/api/calculator/accounts/${id}`, { method: 'DELETE' });
    return handleResponse(response);
  },
  getBillChecks: async (month) => {
    const response = await fetch(`${API_BASE_URL}/api/calculator/bill-checks?month=${month}`);
    return handleResponse(response);
  },
  setBillCheck: async (month, itemKey, paid) => {
    const response = await fetch(`${API_BASE_URL}/api/calculator/bill-checks`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ month, item_key: itemKey, paid }),
    });
    return handleResponse(response);
  },
  suggestAllocation: async (data) => {
    const response = await fetch(`${API_BASE_URL}/api/calculator/suggest`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  // Insurance policies
  getInsurance: async () => {
    const response = await fetch(`${API_BASE_URL}/api/insurance`);
    return handleResponse(response);
  },
  createInsurance: async (data) => {
    const response = await fetch(`${API_BASE_URL}/api/insurance`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  updateInsurance: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/api/insurance/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  deleteInsurance: async (id) => {
    const response = await fetch(`${API_BASE_URL}/api/insurance/${id}`, { method: 'DELETE' });
    return handleResponse(response);
  },

  // AI-maintained per-person payoff plan (persisted)
  getFinancialPlan: async (person) => {
    const response = await fetch(`${API_BASE_URL}/api/reports/plan?person=${encodeURIComponent(person)}`);
    return handleResponse(response);
  },
  planChat: async (person, message, includeBusiness = true) => {
    const response = await fetch(`${API_BASE_URL}/api/reports/plan/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ person, message, include_business: includeBusiness }),
    });
    return handleResponse(response);
  },

  // AI suggest payment strategy
  aiSuggestPaymentStrategy: async () => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/ai/suggest-payment-strategy`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  // AI estimate minimum payment
  aiEstimateMinimumPayment: async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/ai/estimate-minimum-payment/${accountId}`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  // --- Payment reminders ---
  getReminderSettings: async () => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/reminder-settings`);
    return handleResponse(response);
  },

  updateReminderSettings: async (data) => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/reminder-settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  sendTestReminder: async () => {
    const response = await fetch(`${API_BASE_URL}/api/debt-tracker/reminders/test`, { method: 'POST' });
    return handleResponse(response);
  },

  getNotificationCapabilities: async () => {
    const response = await fetch(`${API_BASE_URL}/api/notifications/capabilities`);
    return handleResponse(response);
  },
};

// User management API (super-admin only)
export const userAPI = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/api/users/`);
    return handleResponse(response);
  },

  updateRole: async (userId, role) => {
    const response = await fetch(`${API_BASE_URL}/api/users/${userId}/role`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    });
    return handleResponse(response);
  },

  // page_access: null = every page, or an array of page keys from config/pages.js
  updatePageAccess: async (userId, pageAccess) => {
    const response = await fetch(`${API_BASE_URL}/api/users/${userId}/pages`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page_access: pageAccess }),
    });
    return handleResponse(response);
  },

  getPageCatalog: async () => {
    const response = await fetch(`${API_BASE_URL}/api/users/pages/catalog`);
    return handleResponse(response);
  },

  createUser: async ({ email, name, role, page_access = null }) => {
    const response = await fetch(`${API_BASE_URL}/api/users/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name, role, page_access }),
    });
    return handleResponse(response);
  },

  deactivate: async (userId) => handleResponse(
    await fetch(`${API_BASE_URL}/api/users/${userId}/deactivate`, { method: 'POST' })
  ),

  reactivate: async (userId) => handleResponse(
    await fetch(`${API_BASE_URL}/api/users/${userId}/reactivate`, { method: 'POST' })
  ),

  // Irreversible — removes the profile and the Firebase login. confirmEmail must match.
  deletePermanently: async (userId, confirmEmail) => handleResponse(
    await fetch(
      `${API_BASE_URL}/api/users/${userId}/permanent?confirm_email=${encodeURIComponent(confirmEmail)}`,
      { method: 'DELETE' },
    )
  ),

  sendPasswordReset: async (userId) => handleResponse(
    await fetch(`${API_BASE_URL}/api/users/${userId}/send-password-reset`, { method: 'POST' })
  ),
};

// Dashboard API — one call for the whole landing page. Sections the signed-in user
// isn't allowed to see come back as null rather than erroring.
export const dashboardAPI = {
  getSummary: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const response = await fetch(`${API_BASE_URL}/api/dashboard/summary${qs ? `?${qs}` : ''}`);
    return handleResponse(response);
  },
};

// Invitations — the only way an account is created (super-admin only)
export const invitationAPI = {
  list: async () => handleResponse(await fetch(`${API_BASE_URL}/api/invitations/`)),

  create: async (payload) => handleResponse(await fetch(`${API_BASE_URL}/api/invitations/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })),

  resend: async (id) => handleResponse(await fetch(`${API_BASE_URL}/api/invitations/${id}/resend`, {
    method: 'POST',
  })),

  revoke: async (id) => handleResponse(await fetch(`${API_BASE_URL}/api/invitations/${id}/revoke`, {
    method: 'POST',
  })),
};

// Role templates (super-admin only)
export const roleAPI = {
  list: async () => handleResponse(await fetch(`${API_BASE_URL}/api/roles/`)),

  create: async (payload) => handleResponse(await fetch(`${API_BASE_URL}/api/roles/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })),

  update: async (id, payload) => handleResponse(await fetch(`${API_BASE_URL}/api/roles/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })),

  remove: async (id) => handleResponse(await fetch(`${API_BASE_URL}/api/roles/${id}`, {
    method: 'DELETE',
  })),

  assign: async (userId, roleId) => {
    const qs = roleId ? `?role_id=${roleId}` : '';
    return handleResponse(await fetch(`${API_BASE_URL}/api/roles/assign/${userId}${qs}`, {
      method: 'PATCH',
    }));
  },
};

// Admin console API (super-admin only, except session-policy)
export const adminAPI = {
  getSystem: async () => {
    const response = await fetch(`${API_BASE_URL}/api/admin/system`);
    return handleResponse(response);
  },

  getSecurity: async () => {
    const response = await fetch(`${API_BASE_URL}/api/admin/security`);
    return handleResponse(response);
  },

  getSettings: async () => {
    const response = await fetch(`${API_BASE_URL}/api/admin/settings`);
    return handleResponse(response);
  },

  updateSettings: async (values) => {
    const response = await fetch(`${API_BASE_URL}/api/admin/settings`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ values }),
    });
    return handleResponse(response);
  },

  // Readable by any signed-in user — drives the idle-logout timer.
  getSessionPolicy: async () => {
    const response = await fetch(`${API_BASE_URL}/api/admin/session-policy`);
    return handleResponse(response);
  },
};

// Activity / audit log API (super-admin only)
export const activityAPI = {
  list: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_BASE_URL}/api/activity/${queryString ? `?${queryString}` : ''}`);
    return handleResponse(response);
  },
};

const jsonHeaders = { 'Content-Type': 'application/json' };

export const subscriptionsAPI = {
  getAll: async () => handleResponse(await fetch(`${API_BASE_URL}/api/subscriptions`)),
  create: async (data) => handleResponse(await fetch(`${API_BASE_URL}/api/subscriptions`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify(data),
  })),
  update: async (id, data) => handleResponse(await fetch(`${API_BASE_URL}/api/subscriptions/${id}`, {
    method: 'PUT', headers: jsonHeaders, body: JSON.stringify(data),
  })),
  remove: async (id) => handleResponse(await fetch(`${API_BASE_URL}/api/subscriptions/${id}`, { method: 'DELETE' })),
  // "Rethink these" apply/ignore state (wealth page)
  getRethinkDecisions: async () => handleResponse(await fetch(`${API_BASE_URL}/api/subscriptions/rethink-decisions`)),
  setRethinkDecision: async (itemKey, decision) => handleResponse(await fetch(`${API_BASE_URL}/api/subscriptions/rethink-decisions`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify({ item_key: itemKey, decision }),
  })),
  regenerateRethink: async () => handleResponse(await fetch(`${API_BASE_URL}/api/subscriptions/rethink-decisions/regenerate`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify({}),
  })),
};

export const budgetAPI = {
  getCategories: async () => handleResponse(await fetch(`${API_BASE_URL}/api/budget/categories`)),
  createCategory: async (data) => handleResponse(await fetch(`${API_BASE_URL}/api/budget/categories`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify(data),
  })),
  updateCategory: async (id, data) => handleResponse(await fetch(`${API_BASE_URL}/api/budget/categories/${id}`, {
    method: 'PUT', headers: jsonHeaders, body: JSON.stringify(data),
  })),
  deleteCategory: async (id) => handleResponse(await fetch(`${API_BASE_URL}/api/budget/categories/${id}`, { method: 'DELETE' })),
  getSettings: async () => handleResponse(await fetch(`${API_BASE_URL}/api/budget/settings`)),
  updateSettings: async (data) => handleResponse(await fetch(`${API_BASE_URL}/api/budget/settings`, {
    method: 'PUT', headers: jsonHeaders, body: JSON.stringify(data),
  })),
};

// Notification recipients — who receives the weekly report / reminders / etc.
export const recipientsAPI = {
  getAll: async () => handleResponse(await fetch(`${API_BASE_URL}/api/reminder-recipients`)),
  create: async (data) => handleResponse(await fetch(`${API_BASE_URL}/api/reminder-recipients`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify(data),
  })),
  update: async (id, data) => handleResponse(await fetch(`${API_BASE_URL}/api/reminder-recipients/${id}`, {
    method: 'PUT', headers: jsonHeaders, body: JSON.stringify(data),
  })),
  remove: async (id) => handleResponse(await fetch(`${API_BASE_URL}/api/reminder-recipients/${id}`, { method: 'DELETE' })),
  test: async (id) => handleResponse(await fetch(`${API_BASE_URL}/api/reminder-recipients/${id}/test`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify({}),
  })),
};

// Public, token-authenticated bill-confirmation page (the "did you pay?" follow-up).
export const payConfirmAPI = {
  get: async (token) => handleResponse(await fetch(`${API_BASE_URL}/api/pay-confirm/${token}`)),
  submit: async (token, items) => handleResponse(await fetch(`${API_BASE_URL}/api/pay-confirm/${token}`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify({ items }),
  })),
};


// Assistant workspace — tasks, daily log, ideas board, private notes.
// One prefix for the whole workspace, matching the single `assistant` page key.
export const assistantAPI = {
  getPeople: async () => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/people`)),

  // --- tasks ---
  getTasks: async (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      (Array.isArray(v) ? v : [v]).forEach((one) => qs.append(k, one));
    });
    const q = qs.toString();
    return handleResponse(await fetch(`${API_BASE_URL}/api/assistant/tasks${q ? `?${q}` : ''}`));
  },
  getTaskSummary: async () => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/tasks/summary`)),
  getTask: async (id) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/tasks/${id}`)),
  createTask: async (data) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/tasks`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify(data),
  })),
  updateTask: async (id, data) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/tasks/${id}`, {
    method: 'PUT', headers: jsonHeaders, body: JSON.stringify(data),
  })),
  setTaskStatus: async (id, data) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/tasks/${id}/status`, {
    method: 'PATCH', headers: jsonHeaders, body: JSON.stringify(data),
  })),
  addTaskComment: async (id, body) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/tasks/${id}/comments`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify({ body }),
  })),
  deleteTask: async (id) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/tasks/${id}`, { method: 'DELETE' })),

  // --- daily log ---
  getLog: async (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    ).toString();
    return handleResponse(await fetch(`${API_BASE_URL}/api/assistant/log${qs ? `?${qs}` : ''}`));
  },
  createLogEntry: async (data) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/log`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify(data),
  })),
  updateLogEntry: async (id, data) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/log/${id}`, {
    method: 'PUT', headers: jsonHeaders, body: JSON.stringify(data),
  })),
  deleteLogEntry: async (id) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/log/${id}`, { method: 'DELETE' })),

  // --- private notes (author-scoped server-side) ---
  getNotes: async () => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/notes`)),
  createNote: async (body) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/notes`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify({ body }),
  })),
  updateNote: async (id, body) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/notes/${id}`, {
    method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ body }),
  })),
  deleteNote: async (id) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/notes/${id}`, { method: 'DELETE' })),

  // --- ideas board ---
  getIdeas: async (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    ).toString();
    return handleResponse(await fetch(`${API_BASE_URL}/api/assistant/board${qs ? `?${qs}` : ''}`));
  },
  getIdea: async (id) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/board/${id}`)),
  createIdea: async (data) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/board`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify(data),
  })),
  updateIdea: async (id, data) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/board/${id}`, {
    method: 'PUT', headers: jsonHeaders, body: JSON.stringify(data),
  })),
  addIdeaComment: async (id, body) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/board/${id}/comments`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify({ body }),
  })),
  convertIdea: async (id, data) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/board/${id}/convert`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify(data),
  })),
  deleteIdea: async (id) => handleResponse(await fetch(`${API_BASE_URL}/api/assistant/board/${id}`, { method: 'DELETE' })),
};

// Sales CRM: the lead pipeline board and the verified project reference list.
// Both live under /api/crm (see backend/routers/crm.py).
export const crmAPI = {
  // --- pipeline ---
  getPipeline: async (includeClosed = true) =>
    handleResponse(await fetch(`${API_BASE_URL}/api/crm/pipeline?include_closed=${includeClosed}`)),

  createLead: async (data) => handleResponse(await fetch(`${API_BASE_URL}/api/crm/pipeline`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  })),

  updateLead: async (clientId, data) =>
    handleResponse(await fetch(`${API_BASE_URL}/api/crm/pipeline/${clientId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    })),

  // One-tap "I just spoke to them" — resets days-since-contact.
  logContact: async (clientId) =>
    handleResponse(await fetch(`${API_BASE_URL}/api/crm/pipeline/${clientId}/touch`, { method: 'POST' })),

  // Everything needing a chase: follow-ups due/overdue plus leads gone quiet.
  getFollowUps: async (days = 7) =>
    handleResponse(await fetch(`${API_BASE_URL}/api/crm/follow-ups?days=${days}`)),

  // --- verified project reference list ---
  getProjects: async () => handleResponse(await fetch(`${API_BASE_URL}/api/crm/projects`)),

  createProject: async (data) => handleResponse(await fetch(`${API_BASE_URL}/api/crm/projects`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  })),

  updateProject: async (id, data) => handleResponse(await fetch(`${API_BASE_URL}/api/crm/projects/${id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  })),

  verifyProject: async (id) =>
    handleResponse(await fetch(`${API_BASE_URL}/api/crm/projects/${id}/verify`, { method: 'POST' })),

  deleteProject: async (id) =>
    handleResponse(await fetch(`${API_BASE_URL}/api/crm/projects/${id}`, { method: 'DELETE' })),
};
